import type { MissionCategory, MissionDifficulty, MissionRequirement } from '@/state/types';

/**
 * Spawnable mission templates. The generator picks a random entry weighted
 * by difficulty (MISSION_DIFFICULTY_WEIGHTS) and instantiates a Mission from it.
 *
 * `id` doubles as the dispatch code shown in the UI.
 */
export interface EscalationRule {
  /** Catalog id of the next-tier mission this entry escalates to. */
  toCode: string;
  /** Time in ms (gameTime), measured from lastEscalatedAt ?? spawnedAt. */
  triggerAfterMs: number;
  /**
   * Trigger condition class:
   *  - 'open'              — only escalates while still un-dispatched
   *  - 'noScene'           — open OR dispatched without any vehicle in Status 4
   *  - 'lowProgress'       — already onscene but progress hasn't reached the
   *                          ESCALATION_PROGRESS_FLOOR threshold by the time elapses
   */
  trigger: 'open' | 'noScene' | 'lowProgress';
}

export interface MissionCatalogEntry {
  id: string;
  /** Short dispatch code, e.g. "F2". Same value as id. */
  code: string;
  title: string;
  category: MissionCategory;
  difficulty: MissionDifficulty;
  requiredVehicles: MissionRequirement[];
  /** Number of patients needing transport (Status 7/8 trigger). */
  patientCount: number;
  /** How long Status 4 (on-scene work) takes, in game-minutes. */
  durationMinutes: number;
  /** Liters of water that must be pumped to clear the mission. Fire only. */
  waterRequiredLiters?: number;
  /** True if AGT (Atemschutz) is needed — relevant for resource drain (5.4). */
  breathingAirRequired?: boolean;
  /** Phase 6.1 — when set, this mission can escalate to a more severe code. */
  escalation?: EscalationRule;
  /** Phase 8.4.8 — Caller statement shown on the Einsatzdetails-Tab. */
  callerStatement?: string;
}

export const MISSION_CATALOG: ReadonlyArray<MissionCatalogEntry> = [
  /* ─────────── Brände (F) ─────────── */
  {
    id: 'F1',
    code: 'F1',
    title: 'Kleinbrand',
    category: 'fire',
    difficulty: 'easy',
    requiredVehicles: [{ type: 'LF', count: 1 }],
    patientCount: 0,
    durationMinutes: 8,
    waterRequiredLiters: 1500,
    breathingAirRequired: true,
    escalation: { toCode: 'F2', triggerAfterMs: 4 * 60_000, trigger: 'open' },
    callerStatement:
      '„Bei uns im Hof brennt eine Mülltonne, das Feuer schlägt schon ans Garagentor!"',
  },
  {
    id: 'F2',
    code: 'F2',
    title: 'Zimmerbrand',
    category: 'fire',
    difficulty: 'medium',
    requiredVehicles: [
      { type: 'LF', count: 1 },
      { type: 'DLK', count: 1 },
    ],
    patientCount: 1,
    durationMinutes: 12,
    waterRequiredLiters: 4000,
    breathingAirRequired: true,
    escalation: { toCode: 'F3', triggerAfterMs: 6 * 60_000, trigger: 'noScene' },
    callerStatement:
      '„Aus dem Fenster meiner Nachbarn quillt dichter Rauch. Ich höre niemanden mehr antworten."',
  },
  {
    id: 'F3',
    code: 'F3',
    title: 'Gebäudebrand',
    category: 'fire',
    difficulty: 'hard',
    requiredVehicles: [
      { type: 'LF', count: 2 },
      { type: 'DLK', count: 1 },
      { type: 'ELW', count: 1 },
    ],
    patientCount: 2,
    durationMinutes: 18,
    waterRequiredLiters: 8000,
    breathingAirRequired: true,
    escalation: { toCode: 'F4', triggerAfterMs: 10 * 60_000, trigger: 'lowProgress' },
    callerStatement:
      '„Das ganze Mehrfamilienhaus steht offenbar in Flammen. Im 2. Stock winken Leute aus dem Fenster!"',
  },
  {
    id: 'F4',
    code: 'F4',
    title: 'Großbrand Industrie',
    category: 'fire',
    difficulty: 'expert',
    requiredVehicles: [
      { type: 'LF', count: 2 },
      { type: 'TLF', count: 1 },
      { type: 'DLK', count: 1 },
      { type: 'ELW', count: 1 },
      { type: 'GW', count: 1 },
    ],
    patientCount: 3,
    durationMinutes: 30,
    waterRequiredLiters: 20_000,
    breathingAirRequired: true,
    callerStatement:
      '„In unserem Lager brennt es lichterloh – ich glaube, dort sind Gefahrstoffe gelagert!"',
  },
  {
    id: 'F5',
    code: 'F5',
    title: 'Vegetationsbrand',
    category: 'fire',
    difficulty: 'extreme',
    requiredVehicles: [
      { type: 'LF', count: 2 },
      { type: 'TLF', count: 2 },
      { type: 'ELW', count: 1 },
      { type: 'SW', count: 1 },
    ],
    patientCount: 0,
    durationMinutes: 45,
    waterRequiredLiters: 35_000,
    breathingAirRequired: false,
    callerStatement:
      '„Hier am Waldrand brennt ein riesiges Feld, der Wind treibt das Feuer Richtung Ortschaft."',
  },

  /* ─────────── Hilfeleistungen (H) ─────────── */
  {
    id: 'H1',
    code: 'H1',
    title: 'Ölspur',
    category: 'technical',
    difficulty: 'easy',
    requiredVehicles: [{ type: 'LF', count: 1 }],
    patientCount: 0,
    durationMinutes: 4,
    callerStatement:
      '„Auf der Hauptstraße zieht sich eine lange Ölspur, mehrere Autos sind schon ins Schleudern gekommen."',
  },
  {
    id: 'H2',
    code: 'H2',
    title: 'VU PKW',
    category: 'technical',
    difficulty: 'easy',
    requiredVehicles: [{ type: 'LF', count: 1 }],
    patientCount: 1,
    durationMinutes: 8,
    escalation: { toCode: 'H3', triggerAfterMs: 6 * 60_000, trigger: 'noScene' },
    callerStatement:
      '„Hier vor mir ist gerade ein Auto gegen einen Baum gefahren. Die Fahrerin sitzt benommen drin."',
  },
  {
    id: 'H3',
    code: 'H3',
    title: 'VU eingeklemmte Person',
    category: 'technical',
    difficulty: 'hard',
    requiredVehicles: [
      { type: 'HLF', count: 1 },
      { type: 'RW', count: 1 },
      { type: 'RTW', count: 1 },
      { type: 'NEF', count: 1 },
    ],
    patientCount: 1,
    durationMinutes: 15,
    callerStatement:
      '„Schwerer Verkehrsunfall! Eine Person ist im Auto eingeklemmt – kommt schnell!"',
  },
  {
    id: 'H4',
    code: 'H4',
    title: 'Brandmeldeanlage ausgelöst',
    category: 'technical',
    difficulty: 'easy',
    requiredVehicles: [{ type: 'LF', count: 1 }],
    patientCount: 0,
    durationMinutes: 4,
    breathingAirRequired: false,
    callerStatement:
      '„Bei uns im Bürogebäude geht die Brandmeldeanlage. Wir sehen aber kein Feuer und keinen Rauch."',
  },
  /* Phase 9.6.5 — Schwerlast-Rettung benötigt FWK */
  {
    id: 'H5',
    code: 'H5',
    title: 'Schwerlastrettung',
    category: 'technical',
    difficulty: 'hard',
    requiredVehicles: [
      { type: 'HLF', count: 1 },
      { type: 'RW', count: 1 },
      { type: 'FWK', count: 1 },
      { type: 'RTW', count: 1 },
    ],
    patientCount: 1,
    durationMinutes: 18,
    callerStatement:
      '„Hier ist ein LKW umgekippt und blockiert die Straße — eine Person ist eingeklemmt unter der Ladung!"',
  },

  /* ─────────── Rettungsdienst (R) ─────────── */
  {
    id: 'R1',
    code: 'R1',
    title: 'Person verletzt',
    category: 'rescue',
    difficulty: 'easy',
    requiredVehicles: [{ type: 'RTW', count: 1 }],
    patientCount: 1,
    durationMinutes: 6,
    escalation: { toCode: 'R2', triggerAfterMs: 6 * 60_000, trigger: 'noScene' },
    callerStatement:
      '„Mein Vater ist im Bad gestürzt und kommt nicht mehr alleine hoch. Er hat starke Schmerzen."',
  },
  {
    id: 'R2',
    code: 'R2',
    title: 'Reanimation',
    category: 'rescue',
    difficulty: 'hard',
    requiredVehicles: [
      { type: 'RTW', count: 1 },
      { type: 'NEF', count: 1 },
    ],
    patientCount: 1,
    durationMinutes: 12,
    callerStatement:
      '„Mein Mann atmet nicht mehr! Bitte schickt sofort jemanden!"',
  },
  {
    id: 'R3',
    code: 'R3',
    title: 'Internistischer Notfall',
    category: 'rescue',
    difficulty: 'medium',
    requiredVehicles: [
      { type: 'RTW', count: 1 },
      { type: 'NEF', count: 1 },
    ],
    patientCount: 1,
    durationMinutes: 8,
    callerStatement:
      '„Ich habe seit 20 Minuten ein heftiges Engegefühl in der Brust und Schmerzen im linken Arm."',
  },
  {
    id: 'R4',
    code: 'R4',
    title: 'Verkehrsunfall mit Verletzten',
    category: 'rescue',
    difficulty: 'medium',
    requiredVehicles: [
      { type: 'RTW', count: 1 },
      { type: 'NEF', count: 1 },
      { type: 'LF', count: 1 },
    ],
    patientCount: 2,
    durationMinutes: 12,
    callerStatement:
      '„Zwei Autos sind hier gerade frontal zusammengestoßen, mehrere Verletzte sitzen noch in den Wracks."',
  },

  /* ─────────── Polizei (P) — Phase 10.3 ─────────── */
  {
    id: 'P0',
    code: 'P0',
    title: 'Ruhestörung',
    category: 'police',
    difficulty: 'easy',
    requiredVehicles: [{ type: 'FuStW', count: 1 }],
    patientCount: 0,
    durationMinutes: 4,
    callerStatement:
      '„Hier nebenan ist seit Stunden Krach, die hören Musik viel zu laut, ich kann nicht schlafen!"',
    escalation: { toCode: 'P1', triggerAfterMs: 8 * 60_000, trigger: 'open' },
  },
  {
    id: 'P1',
    code: 'P1',
    title: 'Streit zwischen Personen',
    category: 'police',
    difficulty: 'easy',
    requiredVehicles: [{ type: 'FuStW', count: 1 }],
    patientCount: 0,
    durationMinutes: 5,
    callerStatement:
      '„Da streiten sich zwei auf der Straße, die schreien sich an, ich glaub gleich gibt\'s Schläge!"',
    escalation: { toCode: 'P2', triggerAfterMs: 6 * 60_000, trigger: 'noScene' },
  },
  {
    id: 'P2',
    code: 'P2',
    title: 'Häuslicher Streit',
    category: 'police',
    difficulty: 'medium',
    requiredVehicles: [
      { type: 'FuStW', count: 2 },
      { type: 'RTW', count: 1 },
    ],
    patientCount: 1,
    durationMinutes: 10,
    callerStatement:
      '„Hilfe, mein Mann schlägt mich, kommen Sie schnell, bitte!"',
  },
  {
    id: 'P3',
    code: 'P3',
    title: 'Ladendiebstahl',
    category: 'police',
    difficulty: 'medium',
    requiredVehicles: [{ type: 'FuStW', count: 1 }],
    patientCount: 0,
    durationMinutes: 6,
    callerStatement:
      '„Bei uns hat einer was geklaut und ist abgehauen, der lief Richtung Bahnhof!"',
    escalation: { toCode: 'P5', triggerAfterMs: 4 * 60_000, trigger: 'noScene' },
  },
  {
    id: 'P4',
    code: 'P4',
    title: 'Verkehrsunfall mit Fahrerflucht',
    category: 'police',
    difficulty: 'medium',
    requiredVehicles: [
      { type: 'FuStW', count: 1 },
      { type: 'RTW', count: 1 },
    ],
    patientCount: 1,
    durationMinutes: 10,
    callerStatement:
      '„Hier ist ein Auto auf einen Fußgänger drauf, der Fahrer ist abgehauen!"',
  },
  {
    id: 'P5',
    code: 'P5',
    title: 'Trunkenheit am Steuer',
    category: 'police',
    difficulty: 'medium',
    requiredVehicles: [{ type: 'FuStW', count: 1 }],
    patientCount: 0,
    durationMinutes: 7,
    callerStatement:
      '„Da fährt einer total Schlangenlinien und schert in den Gegenverkehr, ich folge ihm!"',
  },
  {
    id: 'P6',
    code: 'P6',
    title: 'Bedrohung mit Waffe',
    category: 'police',
    difficulty: 'hard',
    requiredVehicles: [
      { type: 'FuStW', count: 3 },
      { type: 'GruKW', count: 1 },
    ],
    patientCount: 0,
    durationMinutes: 18,
    callerStatement:
      '„Da steht einer vor dem Supermarkt mit einer Pistole, alle haben Angst!"',
    escalation: { toCode: 'P10', triggerAfterMs: 12 * 60_000, trigger: 'lowProgress' },
  },
  {
    id: 'P7',
    code: 'P7',
    title: 'Demonstration',
    category: 'police',
    difficulty: 'hard',
    requiredVehicles: [
      { type: 'GruKW', count: 3 },
      { type: 'FuStW', count: 2 },
    ],
    patientCount: 0,
    durationMinutes: 25,
    callerStatement:
      '„Hier auf dem Marktplatz versammeln sich gerade hunderte Leute, manche werfen schon Flaschen!"',
    escalation: { toCode: 'P13', triggerAfterMs: 15 * 60_000, trigger: 'lowProgress' },
  },
  {
    id: 'P8',
    code: 'P8',
    title: 'Messerangriff',
    category: 'police',
    difficulty: 'hard',
    requiredVehicles: [
      { type: 'FuStW', count: 2 },
      { type: 'RTW', count: 1 },
    ],
    patientCount: 1,
    durationMinutes: 14,
    callerStatement:
      '„Hier hat einer ein Messer und sticht auf jemanden ein! Bitte schnell!"',
    escalation: { toCode: 'P11', triggerAfterMs: 10 * 60_000, trigger: 'lowProgress' },
  },
  {
    id: 'P9',
    code: 'P9',
    title: 'Bankraub',
    category: 'police',
    difficulty: 'expert',
    requiredVehicles: [
      { type: 'FuStW', count: 4 },
      { type: 'GruKW', count: 1 },
      { type: 'SEKWg', count: 1 },
    ],
    patientCount: 0,
    durationMinutes: 35,
    callerStatement:
      '„Bei uns in der Filiale, die sind maskiert, die haben Waffen, sie nehmen das Geld!"',
  },
  {
    id: 'P10',
    code: 'P10',
    title: 'Geiselnahme',
    category: 'police',
    difficulty: 'extreme',
    requiredVehicles: [
      { type: 'FuStW', count: 4 },
      { type: 'GruKW', count: 2 },
      { type: 'SEKWg', count: 1 },
      { type: 'MEKWg', count: 1 },
      { type: 'RTW', count: 2 },
    ],
    patientCount: 2,
    durationMinutes: 90,
    callerStatement:
      '„Sie halten Leute fest, die wollen rausverhandeln, ist hier in der Bank!"',
  },
  {
    id: 'P11',
    code: 'P11',
    title: 'Amoklage',
    category: 'police',
    difficulty: 'extreme',
    requiredVehicles: [
      { type: 'SEKWg', count: 2 },
      { type: 'GruKW', count: 3 },
      { type: 'FuStW', count: 4 },
      { type: 'RTW', count: 3 },
      { type: 'NEF', count: 1 },
    ],
    patientCount: 3,
    durationMinutes: 60,
    callerStatement:
      '„Schüsse, hier wird geschossen, in der Schule, kommt schnell, ganz schnell!"',
  },
  {
    id: 'P12',
    code: 'P12',
    title: 'Sprengstoffverdacht',
    category: 'police',
    difficulty: 'expert',
    requiredVehicles: [
      { type: 'FuStW', count: 2 },
      { type: 'GruKW', count: 1 },
      { type: 'SEKWg', count: 1 },
    ],
    patientCount: 0,
    durationMinutes: 40,
    callerStatement:
      '„Da ist ein verdächtiger Koffer, niemand hat ihn abgestellt, kann das Bombe sein?"',
  },
  {
    id: 'P13',
    code: 'P13',
    title: 'Großdemonstration',
    category: 'police',
    difficulty: 'expert',
    requiredVehicles: [
      { type: 'GruKW', count: 4 },
      { type: 'WaWe', count: 2 },
      { type: 'FuStW', count: 4 },
    ],
    patientCount: 0,
    durationMinutes: 50,
    callerStatement:
      '„Riesige Menschenmenge, total aufgeheizt, Steine fliegen, Schaufenster gehen kaputt!"',
  },
];

/** Lookup by id/code. Returns null on unknown id. */
export function getMissionCatalogEntry(id: string): MissionCatalogEntry | null {
  return MISSION_CATALOG.find((e) => e.id === id) ?? null;
}

/** All entries of a given difficulty. */
export function entriesOfDifficulty(d: MissionDifficulty): MissionCatalogEntry[] {
  return MISSION_CATALOG.filter((e) => e.difficulty === d);
}
